import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// パスワードポリシー：8文字以上、大小文字・数字・記号を各1つ以上、空白禁止
const pwPolicyRe = /^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

const BindEmailDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  if (!open) return null;

  const pwPolicyOk = pwPolicyRe.test(password);
  const canSubmit =
    !!email && !!password && !!confirmPassword &&
    password === confirmPassword && pwPolicyOk && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('email', email.trim().toLowerCase());
      params.append('password', password);
      params.append('password_confirm', confirmPassword);

      const res = await api.post('/auth/bind_email', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      // トークンを更新して再ログイン状態にする
      if (res.data?.access_token) {
        login(res.data.access_token, 'user', 'email');
      }

      setOk(true);
      onSuccess?.();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || t('login_success.bind_failed');
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-50 w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-coral" />
            <h2 className="text-base font-bold">
              {t('login_success.optional_bind_title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-xs text-slate-500 mb-5 leading-relaxed">
          {t('login_success.optional_bind_desc')}
        </p>

        {ok ? (
          <div className="text-center py-4">
            <div className="text-3xl mb-3">✅</div>
            <p className="text-sm text-emerald-600 font-medium leading-relaxed">
              {t('login_success.bind_success')}
            </p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 rounded-xl bg-coral text-white text-sm font-semibold hover:opacity-90"
            >
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* メールアドレス */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('login_success.email_label')}
              </label>
              <input
                type="email"
                autoComplete="email"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* パスワード */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('login_success.password_label')}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="w-full border rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                >
                  {showPwd ? t('login_success.hide') : t('login_success.show')}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {t('login_success.password_rule_hint')}
              </p>
              {password && !pwPolicyOk && (
                <p className="text-[11px] text-red-500 mt-0.5">
                  {t('login_success.invalid_password')}
                </p>
              )}
            </div>

            {/* パスワード確認 */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('login_success.confirm_password_label')}
              </label>
              <input
                type={showPwd ? 'text' : 'password'}
                autoComplete="new-password"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-[11px] text-red-500 mt-0.5">
                  {t('login_success.password_mismatch')}
                </p>
              )}
            </div>

            {/* エラー表示 */}
            {err && <p className="text-sm text-red-600">{err}</p>}

            {/* 送信ボタン */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={classNames(
                'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                canSubmit
                  ? 'bg-coral hover:opacity-90 active:scale-[0.98]'
                  : 'bg-slate-300 cursor-not-allowed'
              )}
            >
              {loading
                ? t('login_success.binding')
                : t('login_success.bind_button')}
            </button>

            <p className="text-[10px] text-slate-400 text-center leading-relaxed">
              {t('login_success.agree_tos')}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BindEmailDialog;
